/**
 * Bridges legacy vs new incident report **field ids** when building Tfact issues.
 *
 * ai_assist is expected to return **either** old-key payloads **or** new-key
 * payloads per field pair—not both keys for the same logical field in one object.
 *
 * - Tfact template `item.id` may still be **legacy** or already **new**; the
 *   report from chat may use the other naming. `pickReportValueForTemplate`
 *   resolves that mismatch (new template id + old report key, or the reverse).
 *
 * `mergeIncidentReportForDisplay`: if the UI only has legacy keys in an object,
 * copies into canonical new keys when the canonical slot is empty (read-only
 * display). Does not assume mixed old+new for the same field from ai_assist.
 *
 * TFACT `value` hygiene (defect_trend master UUID, assignee UUID, datetime arrays,
 * title vs top-level `name`): see `coerceTemplateMasterUuidToOptions`,
 * `normalizeIssueDateTimeArrayFields`, `stripIssueTitleFromValue`,
 * `ensureIssuePersonInChargeIsProjectMember`, `retainTfactIssueCustomSchemaProperties`.
 * 課題作成 submit 直前の変換は `applyIssueValueCreatePipeline`、マスタ ID 解決は
 * `resolveMasterIds` / `buildProjectAssigneeResolution`、UUID 定数は `TFACT_MASTER_UUID_REGEX`。
 */

function isPresent(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/** Template field id is legacy → read from new report key first, then legacy. */
const LEGACY_TEMPLATE_ID_TO_NEW_REPORT_KEY: Record<string, string> = {
  date_time_occurrence: "occurrence_date",
  reporting_date: "issue_report_date",
  issue_defect_trend: "defect_trend",
  incidence_rate: "incidence",
};

/** Template field id is new → also accept legacy report keys. */
const NEW_TEMPLATE_ID_TO_LEGACY_REPORT_KEYS: Record<string, readonly string[]> =
  {
    occurrence_date: ["date_time_occurrence"],
    issue_report_date: ["reporting_date"],
    defect_trend: ["issue_defect_trend"],
    incidence: ["incidence_rate"],
  };

const LEGACY_TO_CANONICAL_KEY_MAP: Record<string, string> = {
  date_time_occurrence: "occurrence_date",
  reporting_date: "issue_report_date",
  issue_defect_trend: "defect_trend",
  incidence_rate: "incidence",
};

/**
 * Resolve a value from the incident report object for a given Tfact template
 * field id (`item.id` from issue template).
 */
export function pickReportValueForTemplate(
  reportObj: Record<string, unknown> | null | undefined,
  templateFieldId: string
): unknown {
  if (!reportObj || typeof reportObj !== "object") return undefined;

  const read = (id: string) => reportObj[id];

  // Template still uses a **legacy** field id: prefer **new** report key, then legacy.
  const newKey = LEGACY_TEMPLATE_ID_TO_NEW_REPORT_KEY[templateFieldId];
  if (newKey) {
    const vNew = read(newKey);
    if (isPresent(vNew)) return vNew;
    const vLegacy = read(templateFieldId);
    if (isPresent(vLegacy)) return vLegacy;
    return undefined;
  }

  // Template uses **new** id: prefer it, then any legacy aliases on the report.
  const vPrimary = read(templateFieldId);
  if (isPresent(vPrimary)) return vPrimary;

  const fallbacks = NEW_TEMPLATE_ID_TO_LEGACY_REPORT_KEYS[templateFieldId];
  if (fallbacks) {
    for (const fb of fallbacks) {
      const v = read(fb);
      if (isPresent(v)) return v;
    }
  }
  return undefined;
}

/**
 * Merge legacy keys into canonical keys for read-only UI (SchemaRenderer).
 */
export function mergeIncidentReportForDisplay(
  data: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!data || typeof data !== "object") return {};
  const out: Record<string, unknown> = { ...data };
  const pairs: [string, string][] = [
    ["occurrence_date", "date_time_occurrence"],
    ["issue_report_date", "reporting_date"],
    ["defect_trend", "issue_defect_trend"],
    ["incidence", "incidence_rate"],
  ];
  for (const [canonical, legacy] of pairs) {
    if (!isPresent(out[canonical]) && isPresent(out[legacy])) {
      out[canonical] = out[legacy];
    }
  }

  // ai_assist が課題メタを prerequisite の箇条書きに埋め込む場合、トップレベルが空のままになる。
  // 表示・課題作成前編集のため、未設定のキーだけ prerequisite から補完する。
  hydrateIssueMetaFromPrerequisiteBullets(out);
  return out;
}

/** `- ラベル：値` 形式（全角/半角コロン）の1行を抽出 */
function matchPrerequisiteBullet(
  prerequisite: string,
  labelBeforeColon: string
): string | undefined {
  const esc = labelBeforeColon.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(
    `^-\\s*${esc}\\s*[：:]\\s*(.+)\\s*$`,
    "m"
  );
  const m = prerequisite.match(re);
  const v = m?.[1]?.trim();
  return v || undefined;
}

function tryParseBracketedJsonArray(raw: string): string[] | undefined {
  const t = raw.trim();
  if (!t.startsWith("[")) return undefined;
  try {
    const p = JSON.parse(t) as unknown;
    if (Array.isArray(p)) return p.map((x) => String(x));
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * prerequisite 内「課題管理用メタ情報」付近の箇条書きから、空の canonical フィールドを埋める。
 * （TFACT が UUID を要求する項目は文字列のまま入る場合があり、その後の UI／API で調整される）
 */
export function hydrateIssueMetaFromPrerequisiteBullets(
  out: Record<string, unknown>
): void {
  const preRaw = out.prerequisite;
  if (typeof preRaw !== "string" || !preRaw.trim()) return;

  const setIfEmpty = (key: string, value: unknown) => {
    if (isPresent(out[key])) return;
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    out[key] = value;
  };

  setIfEmpty("issue_status", matchPrerequisiteBullet(preRaw, "課題ステータス"));
  setIfEmpty("issue_type", matchPrerequisiteBullet(preRaw, "課題タイプ"));
  setIfEmpty("issue_person_in_charge", matchPrerequisiteBullet(preRaw, "担当者"));
  setIfEmpty("issue_severity", matchPrerequisiteBullet(preRaw, "重要度"));
  setIfEmpty("issue_priority", matchPrerequisiteBullet(preRaw, "優先度"));
  setIfEmpty("defect_trend", matchPrerequisiteBullet(preRaw, "不具合傾向"));

  const reportDateRaw = matchPrerequisiteBullet(preRaw, "報告日（現在時刻・UTC換算）");
  if (reportDateRaw) {
    const parsed = tryParseBracketedJsonArray(reportDateRaw);
    if (parsed?.length) setIfEmpty("issue_report_date", parsed);
  }
  const reportDateLoose = preRaw.match(
    /^-\s*報告日[^：:\n]*[：:]\s*(.+)\s*$/m
  )?.[1];
  if (reportDateLoose && !isPresent(out.issue_report_date)) {
    const parsed = tryParseBracketedJsonArray(reportDateLoose.trim());
    if (parsed?.length) out.issue_report_date = parsed;
  }
}

/**
 * TFACT `issue_custom_schema.properties` に定義されたキーのみ（インシデントレポートの
 * 追加項目や AI 専用フィールドなど、課題 value に含めてはいけないものを落とす）。
 * 順序はスキーマ定義に揃え、差分比較やログの安定化に寄与する。
 */
export const TFACT_ISSUE_CUSTOM_SCHEMA_PROPERTY_KEYS = [
  "bts_id",
  "remarks",
  "incidence",
  "bts_status",
  "issue_type",
  "defect_trend",
  "issue_status",
  "prerequisite",
  "issue_priority",
  "issue_severity",
  "expected_result",
  "occurrence_date",
  "operating_result",
  "issue_report_date",
  "operating_procedure",
  "issue_person_in_charge",
] as const;

const _TFACT_ISSUE_SCHEMA_KEY_SET = new Set<string>(
  TFACT_ISSUE_CUSTOM_SCHEMA_PROPERTY_KEYS
);

/** @internal テストや呼び出し元でのキー妥当性確認用 */
export function isTfactIssueCustomSchemaPropertyKey(key: string): boolean {
  return _TFACT_ISSUE_SCHEMA_KEY_SET.has(key);
}

/**
 * 課題作成 `value` を TFACT issue_custom_schema の properties のみに絞り込む。
 * （例: `title`, `issue_ai_status`, `issue_test_execution_environment` は別経路のため除外）
 */
/**
 * 課題作成時に `remarks` へ追記する仕様関連フィールド（TFACT value ホワイトリスト外）。
 * 登録用 URL の remarks クエリと同様のラベル形式に揃える。
 */
const SPECIFICATION_FIELDS_APPENDED_TO_REMARKS = [
  {
    key: "results_of_comparison_of_specifications_and_test_cases",
    label: "仕様書とテストケースの比較結果",
  },
  { key: "specifications_applicable_section", label: "仕様書情報" },
] as const;

function readReportTextField(
  obj: Record<string, unknown>,
  key: string
): string {
  const v = obj[key];
  if (typeof v === "string") return v.trim();
  return "";
}

/**
 * `specifications_applicable_section` と
 * `results_of_comparison_of_specifications_and_test_cases` を
 * TFACT 課題の `remarks` に追記する（いずれかに値がある場合のみ）。
 */
export function appendSpecificationReportFieldsToRemarks(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const lines: string[] = [];
  for (const { key, label } of SPECIFICATION_FIELDS_APPENDED_TO_REMARKS) {
    const text = readReportTextField(obj, key);
    if (text) lines.push(`${label}：${text}`);
  }
  if (lines.length === 0) return obj;

  const block = lines.join("\n");
  const out = { ...obj };
  const existing = readReportTextField(out, "remarks");
  if (existing && existing.includes(block)) return out;
  out.remarks = existing ? `${existing}\n\n${block}` : block;
  return out;
}

export function retainTfactIssueCustomSchemaProperties(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of TFACT_ISSUE_CUSTOM_SCHEMA_PROPERTY_KEYS) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      out[key] = obj[key];
    }
  }
  return out;
}

export function canonicalizeIssueValueKeys(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  for (const [legacyKey, canonicalKey] of Object.entries(
    LEGACY_TO_CANONICAL_KEY_MAP
  )) {
    if (
      (out[canonicalKey] === undefined || out[canonicalKey] === null) &&
      out[legacyKey] !== undefined
    ) {
      out[canonicalKey] = out[legacyKey];
    }
    if (legacyKey in out) {
      delete out[legacyKey];
    }
  }
  return out;
}

/** Storage key on the issue `value` object for a TFACT template field id. */
export function toCanonicalIssueValueKey(templateFieldId: string): string {
  return LEGACY_TO_CANONICAL_KEY_MAP[templateFieldId] ?? templateFieldId;
}

/** 課題テンプレートの `data.options` からプルダウン化する項目（レポートタブ） */
export const ISSUE_TEMPLATE_SELECT_FIELD_IDS = [
  "issue_status",
  "issue_type",
  "issue_severity",
  "issue_priority",
  "defect_trend",
] as const;

export type TemplateSelectOption = { value: string; label: string };

/** `TemplateAPI.getTemplate('issue', …)` の戻りから title/fields/attributes 配列を組み立てる。 */
export function parseIssueTemplateItems(
  tplPayload: unknown
): Record<string, unknown>[] {
  if (!tplPayload || typeof tplPayload !== "object") return [];
  const raw = tplPayload as Record<string, unknown>;
  let templateBody: unknown = raw.template;
  if (templateBody === undefined && raw.data && typeof raw.data === "object") {
    templateBody = (raw.data as Record<string, unknown>).template ?? raw.data;
  }
  if (typeof templateBody === "string") {
    try {
      templateBody = JSON.parse(templateBody);
    } catch {
      return [];
    }
  }
  if (!templateBody || typeof templateBody !== "object") return [];
  const t = templateBody as Record<string, unknown>;
  const titleItems = Array.isArray(t.title) ? t.title : [];
  const fieldItems = Array.isArray(t.fields) ? t.fields : [];
  const attrItems = Array.isArray(t.attributes) ? t.attributes : [];
  return [...titleItems, ...fieldItems, ...attrItems] as Record<
    string,
    unknown
  >[];
}

function templateOptionDisplayLabel(row: Record<string, unknown>): string {
  const dn = row.display_name ?? row.displayName;
  if (dn && typeof dn === "object") {
    const d = dn as { ja?: unknown; en?: unknown };
    if (typeof d.ja === "string" && d.ja.trim()) return d.ja.trim();
    if (typeof d.en === "string" && d.en.trim()) return d.en.trim();
  }
  const v = row.value;
  return typeof v === "string" ? v.trim() : "";
}

/**
 * 課題テンプレート項目の `data.options` をフィールド ID ごとのセレクト候補に変換する。
 * `value` は TFACT マスタ UUID、`label` は表示名（日本語優先）。
 */
export function buildTemplateFieldSelectOptionsMap(
  templateItems: readonly Record<string, unknown>[],
  fieldIds: readonly string[] = ISSUE_TEMPLATE_SELECT_FIELD_IDS
): Record<string, TemplateSelectOption[]> {
  const out: Record<string, TemplateSelectOption[]> = {};
  for (const fieldId of fieldIds) {
    const item = templateItems.find((it) => {
      const id = it?.id;
      return (
        typeof id === "string" &&
        (id === fieldId || toCanonicalIssueValueKey(id) === fieldId)
      );
    });
    if (!item) continue;
    const optList = (item.data as { options?: unknown } | undefined)?.options;
    if (!Array.isArray(optList) || optList.length === 0) continue;
    const options: TemplateSelectOption[] = [];
    for (const o of optList) {
      if (!o || typeof o !== "object") continue;
      const row = o as Record<string, unknown>;
      const val = row.value;
      if (typeof val !== "string" || !val.trim()) continue;
      const label = templateOptionDisplayLabel(row);
      options.push({ value: val.trim(), label: label || val.trim() });
    }
    if (options.length > 0) out[fieldId] = options;
  }
  return out;
}

/** AI の日本語ラベル（例: 不具合 / A / 高）をテンプレート option の UUID に寄せる。 */
export function resolveReportValueToTemplateOptionId(
  raw: unknown,
  options: readonly TemplateSelectOption[]
): string | undefined {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s || options.length === 0) return undefined;
  if (options.some((o) => o.value === s)) return s;
  const exactLabel = options.find((o) => o.label === s);
  if (exactLabel) return exactLabel.value;
  const lower = s.toLowerCase();
  const ciLabel = options.find((o) => o.label.toLowerCase() === lower);
  return ciLabel?.value;
}

/**
 * HTTP error fallback copy (issue / TFACT flows). Keep wording in sync with
 * `utils/sstf_client.py` → `_INCIDENT_HTTP_FALLBACK_HINT_JA`.
 */
export const INCIDENT_HTTP_FALLBACK_HINT_JA =
  "必須フィールドやマスタIDの値が無効な可能性があります。レポートをご確認ください。";

/** After template defaults: required fields still null / blank / absent. */
export function formatMissingRequiredIssueFieldsMessageJa(
  labels: readonly string[]
): string {
  const body = labels.filter(Boolean).join("、");
  if (!body) {
    return "必須項目が未入力または無効です。レポートの内容をご確認ください。";
  }
  return `必須項目が未入力または無効です（${body}）。レポートの内容をご確認ください。`;
}

/**
 * When the report left a master reference empty/null but the issue template
 * defines a UUID `initial_value`, copy it onto the canonical key so TFACT
 * validation does not receive `""` / `null` for required masters.
 */
export function applyIssueTemplateInitialValues(
  obj: Record<string, unknown>,
  templateItems: ReadonlyArray<Record<string, unknown>>,
  uuidRegex: RegExp
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  for (const item of templateItems) {
    const tid = item?.id;
    if (typeof tid !== "string" || !tid) continue;
    const canonicalKey = toCanonicalIssueValueKey(tid);
    const initialRaw = item?.initial_value;
    const initial =
      typeof initialRaw === "string" ? initialRaw.trim() : undefined;
    if (!initial || !uuidRegex.test(initial)) continue;

    const cur = out[canonicalKey];
    const curEmpty =
      cur === undefined ||
      cur === null ||
      (typeof cur === "string" && cur.trim() === "");
    if (!curEmpty) continue;

    out[canonicalKey] = initial;
  }
  return out;
}

/**
 * Issue `value` field is still "missing" for required checks after defaults.
 * TFACT allows empty arrays `[]`; do **not** treat them as missing.
 */
export function isBlankRequiredIssueValue(value: unknown): boolean {
  if (value === undefined) return true;
  if (value === null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
}

export type MissingRequiredIssueField = {
  canonicalKey: string;
  templateFieldId: string;
  label: string;
};

function templateItemDisplayLabel(
  item: Record<string, unknown>,
  canonicalKey: string
): string {
  const dn = item.display_name ?? item.displayName;
  if (dn && typeof dn === "object") {
    const o = dn as { ja?: unknown; en?: unknown };
    if (typeof o.ja === "string" && o.ja.trim()) return o.ja.trim();
    if (typeof o.en === "string" && o.en.trim()) return o.en.trim();
  }
  return canonicalKey || "?";
}

/**
 * Template items with `validation.required` must have a non-blank value on the
 * canonical storage key after defaults (null / "" / absent only — `[]` is OK).
 */
export function findMissingRequiredIssueFields(
  valueObj: Record<string, unknown>,
  templateItems: ReadonlyArray<Record<string, unknown>>
): MissingRequiredIssueField[] {
  const missing: MissingRequiredIssueField[] = [];
  for (const item of templateItems) {
    const tid = item?.id;
    if (typeof tid !== "string" || !tid) continue;
    const validation = item.validation as { required?: unknown } | undefined;
    const required = !!(validation && validation.required);
    if (!required) continue;

    const canonicalKey = toCanonicalIssueValueKey(tid);
    // Issue title is sent as top-level `name` on create; `value.title` is stripped intentionally.
    if (canonicalKey === "title" || tid === "title") continue;

    const v = valueObj[canonicalKey];
    if (!isBlankRequiredIssueValue(v)) continue;

    missing.push({
      canonicalKey,
      templateFieldId: tid,
      label: templateItemDisplayLabel(item, canonicalKey),
    });
  }
  return missing;
}

/** Keys on issue `value` that TFACT expects as `date-time` string arrays (ISO with `T`). */
const DEFAULT_ISSUE_DATE_ARRAY_KEYS = [
  "issue_report_date",
  "occurrence_date",
] as const;

function toIsoDateTimeString(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  /** Calendar date only: interpret as local midnight (avoids UTC off-by-one for JST users). */
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [yy, mm, dd] = s.split("-").map((x) => parseInt(x, 10));
    if (
      Number.isFinite(yy) &&
      Number.isFinite(mm) &&
      Number.isFinite(dd)
    ) {
      const d = new Date(yy, mm - 1, dd, 0, 0, 0, 0);
      return Number.isNaN(d.getTime()) ? null : d.toISOString();
    }
    return null;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Normalizes date-like fields to string arrays of ISO 8601 (with `T`).
 * Drops null; omits key if nothing valid remains (empty `[]` is kept when input was `[]`).
 */
export function normalizeIssueDateTimeArrayFields(
  obj: Record<string, unknown>,
  keys: readonly string[] = [...DEFAULT_ISSUE_DATE_ARRAY_KEYS]
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  for (const key of keys) {
    if (!(key in out)) continue;
    const v = out[key];
    if (v === null || v === undefined) {
      delete out[key];
      continue;
    }
    if (typeof v === "string") {
      const iso = toIsoDateTimeString(v);
      if (iso) out[key] = [iso];
      else delete out[key];
      continue;
    }
    if (Array.isArray(v)) {
      const mapped = v
        .filter((x): x is string => typeof x === "string")
        .map((x) => toIsoDateTimeString(x))
        .filter((x): x is string => x != null);
      out[key] = mapped;
      continue;
    }
  }
  return out;
}

/**
 * For template fields with `data.options`, ensure stored UUID is one of the
 * option values (e.g. defect_trend). If not: use template `initial_value` when
 * it is a valid option; otherwise remove the key so required checks can fire.
 */
export function coerceTemplateMasterUuidToOptions(
  obj: Record<string, unknown>,
  templateItems: ReadonlyArray<Record<string, unknown>>,
  uuidRegex: RegExp
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  for (const item of templateItems) {
    const tid = item?.id;
    if (typeof tid !== "string" || !tid) continue;
    const options = item?.data as { options?: unknown } | undefined;
    const optList = options?.options;
    if (!Array.isArray(optList) || optList.length === 0) continue;

    const valid = new Set<string>();
    for (const o of optList) {
      if (!o || typeof o !== "object") continue;
      const val = (o as { value?: unknown }).value;
      if (typeof val === "string" && uuidRegex.test(val)) valid.add(val);
    }
    if (valid.size === 0) continue;

    const canonicalKey = toCanonicalIssueValueKey(tid);
    /** 担当者はプロジェクト API の一覧が正本。テンプレの options が古い/部分集合だと有効 UUID が誤削除されるため除外 */
    if (canonicalKey === "issue_person_in_charge") continue;

    const cur = out[canonicalKey];
    if (typeof cur !== "string" || !uuidRegex.test(cur.trim())) continue;
    const id = cur.trim();
    if (valid.has(id)) continue;

    const initRaw = item?.initial_value;
    const init =
      typeof initRaw === "string" && uuidRegex.test(initRaw.trim())
        ? initRaw.trim()
        : "";
    if (init && valid.has(init)) {
      out[canonicalKey] = init;
    } else {
      delete out[canonicalKey];
    }
  }
  return out;
}

/**
 * テンプレート options の先頭 UUID を canonical キーに入れる（defect_trend / 担当者など共通）。
 */
function pickFirstTemplateOptionUuidForCanonicalKey(
  obj: Record<string, unknown>,
  canonicalKey: string,
  templateItems: ReadonlyArray<Record<string, unknown>>,
  uuidRegex: RegExp
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  if (!isBlankRequiredIssueValue(out[canonicalKey])) return out;

  for (const item of templateItems) {
    const tid = item?.id;
    if (typeof tid !== "string" || !tid) continue;
    if (toCanonicalIssueValueKey(tid) !== canonicalKey) continue;

    const optList = (item?.data as { options?: unknown })?.options;
    if (!Array.isArray(optList)) return out;

    for (const o of optList) {
      if (!o || typeof o !== "object") continue;
      const val = (o as { value?: unknown }).value;
      if (typeof val === "string" && uuidRegex.test(val.trim())) {
        out[canonicalKey] = val.trim();
        return out;
      }
    }
    return out;
  }
  return out;
}

/**
 * Last resort for **defect_trend** only: TFACT needs a real master UUID and cannot
 * infer one. If the report/template left it blank/null but the issue template
 * lists UUID options, use the **first** option so submit passes (same idea as
 * default assignee — verify business meaning in TFACT if needed).
 */
export function fallbackDefectTrendToFirstTemplateOption(
  obj: Record<string, unknown>,
  templateItems: ReadonlyArray<Record<string, unknown>>,
  uuidRegex: RegExp
): Record<string, unknown> {
  return pickFirstTemplateOptionUuidForCanonicalKey(
    obj,
    "defect_trend",
    templateItems,
    uuidRegex
  );
}

/**
 * Last resort for **issue_person_in_charge**: if still blank after assignee
 * resolution / login match, use the first UUID option from the issue template
 * (same pattern as {@link fallbackDefectTrendToFirstTemplateOption}).
 */
export function fallbackIssuePersonInChargeToFirstTemplateOption(
  obj: Record<string, unknown>,
  templateItems: ReadonlyArray<Record<string, unknown>>,
  uuidRegex: RegExp
): Record<string, unknown> {
  return pickFirstTemplateOptionUuidForCanonicalKey(
    obj,
    "issue_person_in_charge",
    templateItems,
    uuidRegex
  );
}

/**
 * TFACT stores the display name on create-issue `name`; remove duplicate `title`
 * from `value` so it is not treated as the canonical title store.
 */
export function stripIssueTitleFromValue(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  if ("title" in out) delete out.title;
  return out;
}

/**
 * Ensure `issue_person_in_charge` is a UUID that belongs to `projectUserIds`
 * (project assignees). Otherwise set `fallbackUserId` if valid and in the project,
 * else set `requestingUserId` when it is a UUID (課題作成リクエスト送信者),
 * else omit the key.
 */
export function ensureIssuePersonInChargeIsProjectMember(
  obj: Record<string, unknown>,
  projectUserIds: ReadonlySet<string>,
  fallbackUserId: string | undefined,
  uuidRegex: RegExp,
  requestingUserId?: string | undefined
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj };
  const raw = out["issue_person_in_charge"];
  const trimmed =
    typeof raw === "string" ? raw.trim() : "";
  const id =
    trimmed && (projectUserIds.has(trimmed) || uuidRegex.test(trimmed))
      ? trimmed
      : undefined;

  if (id && projectUserIds.has(id)) return out;

  if (
    fallbackUserId &&
    (projectUserIds.has(fallbackUserId) || uuidRegex.test(fallbackUserId)) &&
    projectUserIds.has(fallbackUserId)
  ) {
    out["issue_person_in_charge"] = fallbackUserId;
    return out;
  }

  const reqId =
    typeof requestingUserId === "string" ? requestingUserId.trim() : "";
  if (reqId && projectUserIds.has(reqId)) {
    out["issue_person_in_charge"] = reqId;
    return out;
  }

  delete out["issue_person_in_charge"];
  return out;
}

/**
 * 【暫定対応】課題テンプレートで `issue_person_in_charge` が必須かつ未設定のとき、順に試す:
 * 1) ログイン中ユーザーのメール／表示名とプロジェクト担当者一覧を突き合わせて TFACT UUID
 * 2) まだ空ならテンプレート options の先頭 UUID（{@link fallbackIssuePersonInChargeToFirstTemplateOption}）
 * 3) まだ空なら `requestingUserId` が TFACT 形式 UUID のときだけ課題作成依頼者として入れる
 *
 * メール・名前が無い場合でも 2) 3) は試す。`/auth/me` の `id` が Auth0 sub のときは 3) は何もしない。
 * 恒久対応では SSTF の TFACT `user_id` をフロントへ渡し、`requestingUserId` に渡す想定。
 */
export function applyTemporaryIssuePersonInChargeFromLoginUserMatch(params: {
  issueValueObj: Record<string, unknown>;
  issueTemplateItems: ReadonlyArray<Record<string, unknown>>;
  projectAssigneeUsers: ReadonlyArray<Record<string, unknown>>;
  uuidRegex: RegExp;
  loginEmail?: string | null;
  loginName?: string | null;
  requestingUserId?: string | null;
}): Record<string, unknown> {
  const {
    issueValueObj,
    issueTemplateItems,
    projectAssigneeUsers,
    uuidRegex,
    loginEmail,
    loginName,
    requestingUserId,
  } = params;
  const out: Record<string, unknown> = { ...issueValueObj };

  const templateRequiresPerson = issueTemplateItems.some((item) => {
    const tid = item?.id;
    if (typeof tid !== "string") return false;
    if (toCanonicalIssueValueKey(tid) !== "issue_person_in_charge") return false;
    const validation = item.validation as { required?: unknown } | undefined;
    return !!(validation && validation.required);
  });
  if (!templateRequiresPerson) return out;

  if (!isBlankRequiredIssueValue(out["issue_person_in_charge"])) return out;

  const emailNorm = typeof loginEmail === "string" ? loginEmail.trim().toLowerCase() : "";
  const nameNorm = typeof loginName === "string" ? loginName.trim() : "";
  if (!emailNorm && !nameNorm) return out;

  for (const raw of projectAssigneeUsers) {
    const u = raw as { id?: unknown; email?: unknown; name?: unknown };
    const uid = typeof u.id === "string" ? u.id.trim() : "";
    if (!uid || !isTfactEntityId(uid)) continue;
    const uemail =
      typeof u.email === "string" ? u.email.trim().toLowerCase() : "";
    const uname = typeof u.name === "string" ? u.name.trim() : "";
    if (emailNorm && uemail && emailNorm === uemail) {
      out["issue_person_in_charge"] = uid;
      return out;
    }
  }

  let next = fallbackIssuePersonInChargeToFirstTemplateOption(
    out,
    issueTemplateItems,
    uuidRegex
  );

  if (!isBlankRequiredIssueValue(next["issue_person_in_charge"])) return next;

  const reqId =
    typeof requestingUserId === "string" &&
    uuidRegex.test(requestingUserId.trim())
      ? requestingUserId.trim()
      : undefined;
  if (reqId) next["issue_person_in_charge"] = reqId;
  return next;
}

// --- TFACT UUID / JSON / BFF マスタ解決（課題作成フロント用・同一モジュールに集約） ---

/** TFACT / BFF が返すマスタ ID（UUID）の検証用。 */
export const TFACT_MASTER_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isTfactMasterUuid(
  value: string | undefined | null,
  regex: RegExp = TFACT_MASTER_UUID_REGEX
): boolean {
  return typeof value === "string" && regex.test(value.trim());
}

/** Parse JSON to a plain object; never throws. Arrays become `{}`. */
export function parseJsonObject(
  raw: string | null | undefined
): Record<string, unknown> {
  try {
    const o = JSON.parse(raw ?? "{}");
    if (o !== null && typeof o === "object" && !Array.isArray(o)) {
      return o as Record<string, unknown>;
    }
  } catch {
    /* ignore */
  }
  return {};
}

export type MasterRow = Record<string, unknown>;

export function toScalarOrArrayForMasterResolution(value: unknown): unknown[] {
  if (value === undefined || value === null) return [];
  if (typeof value === "string" && value.trim() === "") return [];
  return Array.isArray(value) ? value : [value];
}

export function resolveMasterIds(
  value: unknown,
  masters: MasterRow[],
  idKey: string,
  nameKey: string
): string[] {
  const arr = toScalarOrArrayForMasterResolution(value);
  return arr
    .map((val) => {
      if (typeof val === "string" && masters.some((m) => m[idKey] === val)) {
        return val;
      }
      const s = typeof val === "string" ? val.trim() : String(val);
      const hitById = masters.find((m) => m[idKey] === s);
      if (hitById && typeof hitById[idKey] === "string") {
        return hitById[idKey] as string;
      }
      const hitByName = masters.find(
        (m) => String(m[nameKey] ?? "").trim() === s
      );
      if (hitByName && typeof hitByName[idKey] === "string") {
        return hitByName[idKey] as string;
      }
      return undefined;
    })
    .filter((v): v is string => typeof v === "string");
}

/** `getAssignees` 応答の `users[].user` をフラット配列に */
export function extractAssigneeUserRows(
  assigneesPayload: unknown
): Record<string, unknown>[] {
  if (!assigneesPayload || typeof assigneesPayload !== "object") return [];
  const users = (assigneesPayload as { users?: unknown }).users;
  if (!Array.isArray(users)) return [];
  return users
    .map((row) => {
      if (!row || typeof row !== "object") return undefined;
      const u = (row as { user?: unknown }).user;
      if (u && typeof u === "object" && !Array.isArray(u)) {
        return u as Record<string, unknown>;
      }
      return undefined;
    })
    .filter((x): x is Record<string, unknown> => x != null);
}

export type ProjectAssigneeResolution = {
  projectAssigneeUserIds: Set<string>;
  firstAssigneeUserId: string | undefined;
  assigneeUsersSnapshot: Record<string, unknown>[];
};

export function buildProjectAssigneeResolution(
  userRows: ReadonlyArray<Record<string, unknown>>,
  uuidRegex: RegExp
): ProjectAssigneeResolution {
  const projectAssigneeUserIds = new Set<string>();
  for (const u of userRows) {
    const uid = u.id;
    if (typeof uid === "string" && uuidRegex.test(uid)) {
      projectAssigneeUserIds.add(uid);
    }
  }
  const first = userRows[0];
  const firstId =
    first &&
    typeof first.id === "string" &&
    uuidRegex.test(first.id)
      ? first.id
      : undefined;
  return {
    projectAssigneeUserIds,
    firstAssigneeUserId: firstId,
    assigneeUsersSnapshot: [...userRows],
  };
}

export type IssueValueCreatePipelineInput = {
  issueValueObj: Record<string, unknown>;
  issueTemplateItems: ReadonlyArray<Record<string, unknown>>;
  uuidRegex: RegExp;
  projectAssigneeUserIds: ReadonlySet<string>;
  firstAssigneeUserId: string | undefined;
  assigneeUsersSnapshot: ReadonlyArray<Record<string, unknown>>;
  loginEmail?: string | null;
  loginName?: string | null;
  requestingUserId?: string | null;
};

/**
 * env/build/assignee の BFF 解決後に呼ぶ。必須チェックの前段の純粋変換。
 */
export function applyIssueValueCreatePipeline(
  input: IssueValueCreatePipelineInput
): Record<string, unknown> {
  const {
    issueValueObj,
    issueTemplateItems,
    uuidRegex,
    projectAssigneeUserIds,
    firstAssigneeUserId,
    assigneeUsersSnapshot,
    loginEmail,
    loginName,
    requestingUserId,
  } = input;

  let submitValue = canonicalizeIssueValueKeys({ ...issueValueObj });
  submitValue = applyIssueTemplateInitialValues(
    submitValue,
    issueTemplateItems,
    uuidRegex
  );
  submitValue = coerceTemplateMasterUuidToOptions(
    submitValue,
    issueTemplateItems,
    uuidRegex
  );
  submitValue = fallbackDefectTrendToFirstTemplateOption(
    submitValue,
    issueTemplateItems,
    uuidRegex
  );
  submitValue = normalizeIssueDateTimeArrayFields(submitValue);
  submitValue = ensureIssuePersonInChargeIsProjectMember(
    submitValue,
    projectAssigneeUserIds,
    firstAssigneeUserId,
    uuidRegex,
    requestingUserId ?? undefined
  );
  submitValue = stripIssueTitleFromValue(submitValue);
  submitValue = applyTemporaryIssuePersonInChargeFromLoginUserMatch({
    issueValueObj: submitValue,
    issueTemplateItems,
    projectAssigneeUsers: assigneeUsersSnapshot,
    uuidRegex,
    loginEmail,
    loginName,
    requestingUserId,
  });
  return submitValue;
}

/**
 * TFACT / SSTF が返すエンティティ ID（ユーザー・マスタ UUID 等）。
 * RFC4122 の v1–v5 限定正規表現では `019be9b1-2b59-76e1-...` のような ID を弾いてしまう。
 */
export const TFACT_ENTITY_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** @deprecated use {@link TFACT_ENTITY_ID_RE} */
export const ASSIGNEE_LIST_USER_ID_RE = TFACT_ENTITY_ID_RE;

/** @deprecated use {@link TFACT_ENTITY_ID_RE} */
export const ISSUE_PERSON_IN_CHARGE_UUID_RE = TFACT_ENTITY_ID_RE;

export function isTfactEntityId(value: unknown): value is string {
  return typeof value === "string" && TFACT_ENTITY_ID_RE.test(value.trim());
}

export type ProjectMasterOption = { id: string; name: string };

/** `GET .../build_versions` の apiClient 戻り `data` からビルドマスタを取り出す。 */
export function extractBuildVersionsFromApi(data: unknown): ProjectMasterOption[] {
  if (!data || typeof data !== "object") return [];
  const d = data as Record<string, unknown>;
  const list =
    (Array.isArray(d.build_versions) && d.build_versions) ||
    (d.data &&
      typeof d.data === "object" &&
      Array.isArray((d.data as Record<string, unknown>).build_versions) &&
      (d.data as Record<string, unknown>).build_versions) ||
    [];
  const out: ProjectMasterOption[] = [];
  for (const row of list as Array<Record<string, unknown>>) {
    const id =
      typeof row.build_version_id === "string"
        ? row.build_version_id.trim()
        : "";
    const name = typeof row.name === "string" ? row.name.trim() : "";
    if (!id || !name) continue;
    if (row.enabled === false) continue;
    out.push({ id, name });
  }
  return out.sort((a, b) =>
    a.name.localeCompare(b.name, "ja", { sensitivity: "base" })
  );
}

/** `GET .../execution_environments` の apiClient 戻り `data` から実行環境マスタを取り出す。 */
export function extractExecutionEnvironmentsFromApi(
  data: unknown
): ProjectMasterOption[] {
  if (!data || typeof data !== "object") return [];
  const d = data as Record<string, unknown>;
  const list =
    (Array.isArray(d.test_execution_environments) &&
      d.test_execution_environments) ||
    (d.data &&
      typeof d.data === "object" &&
      Array.isArray(
        (d.data as Record<string, unknown>).test_execution_environments
      ) &&
      (d.data as Record<string, unknown>).test_execution_environments) ||
    [];
  const out: ProjectMasterOption[] = [];
  for (const row of list as Array<Record<string, unknown>>) {
    const id =
      typeof row.execution_environment_id === "string"
        ? row.execution_environment_id.trim()
        : "";
    const name = typeof row.name === "string" ? row.name.trim() : "";
    if (!id || !name) continue;
    out.push({ id, name });
  }
  return out.sort((a, b) =>
    a.name.localeCompare(b.name, "ja", { sensitivity: "base" })
  );
}

/** レポートの文字列（名称・自由記述）をプロジェクトマスタの name に寄せる。 */
export function resolveReportValueToMasterName(
  raw: unknown,
  masters: readonly ProjectMasterOption[]
): string | undefined {
  if (masters.length === 0) return undefined;
  let s = "";
  if (Array.isArray(raw)) {
    const first = raw.find((x) => typeof x === "string" && x.trim());
    s = typeof first === "string" ? first.trim() : "";
  } else if (typeof raw === "string") {
    s = raw.trim();
  }
  if (!s) return undefined;

  const exact = masters.find((m) => m.name === s);
  if (exact) return exact.name;

  const byId = masters.find((m) => m.id === s);
  if (byId) return byId.name;

  const lower = s.toLowerCase();
  const ci = masters.find((m) => m.name.toLowerCase() === lower);
  if (ci) return ci.name;

  const partial = masters.find(
    (m) => s.includes(m.name) || m.name.includes(s)
  );
  return partial?.name;
}

/** ISO 8601 → `<input type="datetime-local">` 用（ローカル時刻）。 */
export function isoToDatetimeLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local の値 → ISO 8601 文字列（1要素配列用）。 */
export function datetimeLocalInputToIso(local: string): string | null {
  const t = local.trim();
  if (!t) return null;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export type AssigneeUser = {
  id: string;
  name: string;
  email?: string;
};

/**
 * `GET .../assignees` の apiClient 戻り `data` から `users` 配列を取り出す（ラップ形式の差異を吸収）。
 */
export function extractAssigneeUsersPayload(data: unknown): unknown {
  if (!data || typeof data !== "object") return undefined;
  const d = data as Record<string, unknown>;
  if (Array.isArray(d.users)) return d.users;
  const inner = d.data;
  if (inner && typeof inner === "object") {
    const u = (inner as Record<string, unknown>).users;
    if (Array.isArray(u)) return u;
  }
  return undefined;
}

export function normalizeAssigneeUsersFromApi(
  usersPayload: unknown
): AssigneeUser[] {
  if (!Array.isArray(usersPayload)) return [];
  const out: AssigneeUser[] = [];
  for (const row of usersPayload) {
    const u = (row as { user?: { id?: unknown; name?: unknown; email?: unknown } })
      ?.user;
    if (!u || typeof u.id !== "string") continue;
    const id = u.id.trim();
    if (!ASSIGNEE_LIST_USER_ID_RE.test(id)) continue;
    out.push({
      id,
      name: typeof u.name === "string" ? u.name.trim() : "",
      email: typeof u.email === "string" ? u.email.trim() : undefined,
    });
  }
  return out;
}

/**
 * AI や手入力の `issue_person_in_charge`（UUID / 表示名 / 空 / 不明）を、
 * プロジェクト担当者一覧とログインユーザー情報から TFACT 用ユーザー UUID に寄せる。
 *
 * 優先順位:
 * 1) 既にプロジェクト担当者に含まれる UUID
 * 2) 表示名の完全一致（trim）
 * 3) 空・不明・未解決時: ログインユーザーを担当者一覧の email / name で突合
 */
export function resolveIssuePersonInChargeToAssigneeUserId(params: {
  raw: unknown;
  assignees: readonly AssigneeUser[];
  loginEmail?: string | null;
  loginName?: string | null;
}): string | undefined {
  const { raw, assignees, loginEmail, loginName } = params;
  if (assignees.length === 0) return undefined;

  const matchLoginUser = (): string | undefined => {
    const emailNorm =
      typeof loginEmail === "string" ? loginEmail.trim().toLowerCase() : "";
    const nameNorm =
      typeof loginName === "string" ? loginName.trim().toLowerCase() : "";
    if (!emailNorm && !nameNorm) return undefined;
    for (const u of assignees) {
      const uemail = (u.email || "").trim().toLowerCase();
      const uname = u.name.trim().toLowerCase();
      if (emailNorm && uemail && emailNorm === uemail) return u.id;
      if (nameNorm && uname && nameNorm === uname) return u.id;
    }
    return undefined;
  };

  const rawStr = typeof raw === "string" ? raw.trim() : "";
  if (!rawStr || rawStr === "不明") {
    return matchLoginUser();
  }

  if (
    isTfactEntityId(rawStr) &&
    assignees.some((a) => a.id === rawStr)
  ) {
    return rawStr;
  }

  const byName = assignees.find((a) => a.name && a.name.trim() === rawStr);
  if (byName) return byName.id;

  return matchLoginUser();
}
